import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, errMsg, newIdemKey, type Account, type Card, type InvoiceSummary } from "../api";
import { fmtDay, formatMoney, normalizeMoneyInput, todayISO } from "../lib/money";
import { useMe } from "../session";
import { Modal } from "./Modal";
import { Btn, Field, Input, Notice, Select } from "./ui";

/** Lançar pagamento da fatura (total ou parcial). Pagamento parcial pode quitar a fatura: o restante vai como "Saldo anterior" da próxima. */
export function PayInvoiceDialog({ card, invoice, onClose, onDone }: { card: Card; invoice: InvoiceSummary; onClose: () => void; onDone: (msg: string) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [amount, setAmount] = useState(invoice.remaining.decimal.replace(".", ","));
  const [paidDate, setPaidDate] = useState(todayISO());
  const [payAccountId, setPayAccountId] = useState(card.payAccountId);
  const [finalSettlement, setFinalSettlement] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idem = useMemo(() => newIdemKey(), []);
  useEffect(() => {
    void api<Account[]>("GET", `/api/${realm}/accounts?entityId=${card.entityId}&status=ativa`).then((a) => setAccounts(a.filter((x) => x.currency === "BRL"))).catch((e) => setErr(errMsg(e)));
  }, [realm, card.entityId]);
  const norm = normalizeMoneyInput(amount);
  const partial = norm !== null && Number(norm) * 100 < invoice.remaining.cents - 0.5;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (norm === null || norm.startsWith("-") || /^0+(\.0+)?$/.test(norm)) return setErr("Informe o valor do pagamento (maior que zero).");
    if (Math.round(Number(norm) * 100) > invoice.remaining.cents) return setErr(`O valor não pode ser maior que o restante da fatura (${formatMoney(invoice.remaining.decimal, "BRL")}).`);
    if (paidDate > todayISO()) return setErr("A data do pagamento não pode ser futura.");
    setBusy(true);
    try {
      await api("POST", `/api/${realm}/cards/${card.id}/pay`, { dueDate: invoice.dueDate, amount: norm, paidDate, payAccountId, finalSettlement: partial && finalSettlement }, idem);
      onDone(partial && finalSettlement ? "Pagamento lançado; o restante passou para a próxima fatura." : "Pagamento da fatura lançado.");
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <Modal title={`Lançar pagamento — fatura de ${fmtDay(invoice.dueDate)}`} onClose={onClose}>
      <form onSubmit={submit} noValidate data-pay-form>
        {err && <Notice kind="error">{err}</Notice>}
        <p className="small muted">Valor a pagar: <b>{formatMoney(invoice.remaining.decimal, "BRL")}</b></p>
        <div className="grid2">
          <Field label="Valor pago (R$)" required><Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" decimals={2} name="amount" /></Field>
          <Field label="Data do pagamento" required><Input type="date" value={paidDate} max={todayISO()} onChange={(e) => setPaidDate(e.target.value)} name="paidDate" /></Field>
        </div>
        <Field label="Conta de pagamento" required>
          <Select value={payAccountId} onChange={(e) => setPayAccountId(e.target.value)} name="payAccount">{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</Select>
        </Field>
        {partial && (
          <label className="check-row"><input type="checkbox" checked={finalSettlement} onChange={(e) => setFinalSettlement(e.target.checked)} name="finalSettlement" /> Quitação final: o restante passa para a próxima fatura como "Saldo anterior"</label>
        )}
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy} data-pay-save>{busy ? "Lançando…" : "Lançar pagamento"}</button></div>
      </form>
    </Modal>
  );
}
